import { useEffect, useRef, useState } from 'react'
import {
  Boxes,
  Building2,
  DraftingCompass,
  Focus,
  FolderOpen,
  PanelRightOpen,
  Ruler,
  type LucideIcon,
} from 'lucide-react'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { useSampleLoad } from '@viewer/lib/useSampleLoad'
import { useMobileUi } from './mobileUiStore'

// ---------------------------------------------------------------------------
// MobileBar: fixed bottom action bar shown instead of the desktop ribbon on
// phone-sized viewports. Carries only the essentials: open the model /
// properties drawers, load an IFC (or a sample), focus the camera, and a
// distance-measure toggle. Everything else stays reachable from inside the
// drawers (spatial tree, model list, properties).
// ---------------------------------------------------------------------------

export default function MobileBar() {
  const fileRef = useRef<HTMLInputElement | null>(null)
  const openDrawer = useMobileUi((s) => s.openDrawer)
  const toggleDrawer = useMobileUi((s) => s.toggleDrawer)
  const setLeftCollapsed = useAppStore((s) => s.setLeftSidebarCollapsed)
  const setRightCollapsed = useAppStore((s) => s.setRightSidebarCollapsed)
  const measureMode = useAppStore((s) => s.measureMode)
  const setMeasureMode = useAppStore((s) => s.setMeasureMode)
  const setAnnotationPlaceMode = useAppStore((s) => s.setAnnotationPlaceMode)
  const modelsCount = useAppStore((s) => s.models.length)
  const viewerSurface = useAppStore((s) => s.viewerSurface)
  const setViewerSurface = useAppStore((s) => s.setViewerSurface)

  const { busyId: sampleBusyId, run: runSample } = useSampleLoad()
  const sampleBusy = sampleBusyId !== null
  const [loaderReady, setLoaderReady] = useState(viewerApi.hasIfcLoader())
  useEffect(
    // The initial value comes from the useState initializer above; the
    // subscription fires (via viewerApi.notify) the moment the loader is
    // registered, so no synchronous setState in the effect body is needed.
    () => viewerApi.subscribe(() => setLoaderReady(viewerApi.hasIfcLoader())),
    [],
  )

  const onOpenModels = () => {
    setLeftCollapsed(false)
    toggleDrawer('models')
  }
  const onOpenProps = () => {
    setRightCollapsed(false)
    toggleDrawer('properties')
  }
  const onMeasure = () => {
    const next = measureMode === 'length-free' ? null : 'length-free'
    if (next) setAnnotationPlaceMode(null)
    setMeasureMode(next)
  }
  return (
    <nav className="flex shrink-0 items-stretch justify-around border-t border-[var(--color-border)] bg-[var(--color-panel)] pb-[env(safe-area-inset-bottom)] text-[var(--color-text)]">
      <input
        ref={fileRef}
        type="file"
        accept=".ifc,.ifczip"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0]
          if (file) await viewerApi.loadIfcFile(file, { replace: true })
          e.target.value = ''
        }}
      />
      <BarBtn
        icon={Boxes}
        label="Modelos"
        active={openDrawer === 'models'}
        onClick={onOpenModels}
      />
      <BarBtn
        icon={FolderOpen}
        label={modelsCount > 0 ? 'Trocar' : 'Abrir'}
        disabled={!loaderReady}
        onClick={() => fileRef.current?.click()}
      />
      <BarBtn
        icon={Building2}
        label={sampleBusy ? 'A abrir…' : 'Exemplo'}
        disabled={!loaderReady || sampleBusy}
        onClick={() => void runSample()}
      />
      <BarBtn
        icon={Focus}
        label="Focar"
        onClick={() => void viewerApi.focusOnSelection()}
      />
      <BarBtn
        icon={Ruler}
        label="Medir"
        active={measureMode === 'length-free'}
        onClick={onMeasure}
      />
      {/* Entrada para o MODO 2D no telemóvel: aqui não há ribbon, e sem
          este botão as plantas e os cortes ficavam inalcançáveis. */}
      <BarBtn
        icon={DraftingCompass}
        label="2D"
        active={viewerSurface === '2d'}
        disabled={modelsCount === 0 && viewerSurface !== '2d'}
        onClick={() => setViewerSurface(viewerSurface === '2d' ? '3d' : '2d')}
      />
      <BarBtn
        icon={PanelRightOpen}
        label="Props"
        active={openDrawer === 'properties'}
        onClick={onOpenProps}
      />
    </nav>
  )
}

interface BarBtnProps {
  icon: LucideIcon
  label: string
  onClick: () => void
  active?: boolean
  disabled?: boolean
}

function BarBtn({ icon: Icon, label, onClick, active, disabled }: BarBtnProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] transition-colors ${
        disabled
          ? 'text-[var(--color-text-dim)] opacity-50'
          : active
            ? 'text-[var(--color-accent)]'
            : 'text-[var(--color-text-muted)] active:bg-[var(--color-bg)]'
      }`}
    >
      <Icon size={20} strokeWidth={1.6} />
      <span className="max-w-full truncate leading-none">{label}</span>
    </button>
  )
}
